import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Link, Paper, Typography } from '@mui/material'
import { ICellRendererParams } from 'ag-grid-community'
import { scaleLinear, scaleLog } from 'd3-scale'
import { ComponentProps, useMemo } from 'react'
import { Link as RouterLink } from 'react-router'
import { useQuery } from 'urql'
import useEventStream from '../contexts/EventStream/useEventStream'
import { graphql } from '../gql'
import { parseCurrencyString } from '../utils/currencyParser'
import { serializeDates } from '../utils/serialize-dates'
import { NameWithFavicon } from './NameWithFavicon'
import ProjectProgressBar from './ProjectProgressBar'
import { ThemedAgGrid } from './ThemedAgGrid'
import { ProjectIcon } from './icon-catalog'

const listQuery = graphql(`
  query ProjectsList {
    Projects {
      id
      name
      dateCreated
      outstandingCommission
      incomePotential
      totalPaymentsReceived
      client {
        name
        website
      }
      rfqs {
        status
        vendor {
          commissionPercentage
        }
        quote {
          amount
          purchaseOrder {
            amount
            commissionPercentage
          }
        }
      }
    }
    Organization{
      clientCount
      vendorCount 
    }
  }
`)

export default function ProjectsList(props: {
  boxProps?: ComponentProps<typeof Box>
  gridProps?: ComponentProps<typeof ThemedAgGrid>
  enableDelete?: boolean
  incomeScale?: 'linear' | 'log'
}) {
  const { incomeScale = 'linear' } = props
  const [result, refetch] = useQuery({ query: listQuery, requestPolicy: 'network-only' })
  const { data, fetching, error } = result

  const maxIncomePotential = useMemo(() => {
    let max = 0
    if (data?.Projects) {
      for (const project of data.Projects) {
        const incomePotential = parseCurrencyString(project.incomePotential)
        if (incomePotential > max) {
          max = incomePotential
        }
      }
    }
    return max
  }, [data?.Projects])

  useEventStream({
    onMessage: (message) => {
      if (message?.event?.startsWith('project.')) {
        setTimeout(() => refetch(), 100)
      }
    },
  })

  if (fetching) return <p>Loading...</p>
  if (error) return <p>Oh no... {error.message}</p>

  if (!data || !data.Projects || !data.Projects.length) {
    return <EmptyList />
  }

  const rowData = data.Projects.map(serializeDates)

  type TData = (typeof rowData)[0]

  return (
    <Box flex="1" {...(props.boxProps || {})}>
      <ThemedAgGrid
        {...(props.gridProps || {})}
        rowData={rowData}
        containerStyle={{ border: 'none' }}
        gridOptions={{
          autoSizeStrategy: {
            type: 'fitGridWidth',
          },
        }}
        columnDefs={[
          {
            field: 'name',
            filter: true,
            cellRenderer: (d: ICellRendererParams<TData>) => {
              return (
                <Link component={RouterLink} to={`/projects/${d.data!.id}`}>
                  {d.data!.name}
                </Link>
              )
            },
          },
          {
            headerName: 'Client',
            valueGetter: (d) => d.data?.client?.name,
            filter: true,
            cellRenderer: (d: ICellRendererParams<TData>) => {
              const c = d.data?.client
              if (!c) return null
              return <NameWithFavicon name={c.name} website={c.website} />
            },
          },
          {
            headerName: '# Orders',
            valueGetter: (d) => d.data?.rfqs?.length,
            width: 80,
          },
          {
            headerName: 'Progress',
            valueGetter: (d) => d.data?.incomePotential,
            cellRenderer: (d: ICellRendererParams<TData>) => {
              if (!d.data) return null
              const incomePotential = parseCurrencyString(d.data.incomePotential)
              const scaleFn = incomeScale === 'log' ? scaleLinear : scaleLog
              const x = scaleFn([500, maxIncomePotential], [20, 80])
              const ratio = incomePotential ? x(incomePotential) : 0
              const width = 20 + ratio
              return (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  height="100%"
                  width={`${width}%`}
                  minWidth="5em"
                >
                  <ProjectProgressBar
                    incomePotential={incomePotential}
                    outstandingCommission={parseCurrencyString(d.data?.outstandingCommission)}
                    totalPaymentsReceived={parseCurrencyString(d.data?.totalPaymentsReceived)}
                  />
                </Box>
              )
            },
          },
          { field: 'dateCreated', filter: true, type: 'rightAligned', resizable: false },
        ]}
      />
    </Box>
  )
}

const EmptyList = () => {
  return (
    <Paper sx={{ minHeight: 200, height: '100%', p: 2 }}>
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
        <ProjectIcon sx={{ fontSize: 60, color: (theme) => theme.palette.grey[300] }} />
        <Typography variant="caption" gutterBottom>
          You don't have any projects yet.
        </Typography>
        <Button
          component={RouterLink}
          to="/projects/new"
          variant="contained"
          color="primary"
          sx={{ mt: 2 }}
          startIcon={<AddIcon />}
        >
          Add a Project
        </Button>
      </Box>
    </Paper>
  )
}
